import jsQR from 'jsqr';

/**
 * Badge photo prep for the booth flow (CONFERENCE-INTAKE): downscale the
 * camera photo to something the API accepts (< 3 MB data URL) and decode the
 * badge QR in the browser — the ACS badge QR holds just the attendee ID
 * (e.g. "HDNRS6PHNQV"), so it never needs to leave the tablet to be read.
 */

const MAX_SIDE = 1600;
const MAX_BYTES = 3 * 1024 * 1024;

function loadImage(file: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("couldn't open that photo"));
    };
    img.src = url;
  });
}

function draw(img: CanvasImageSource, w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('this browser cannot process photos');
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

/** Bytes a base64 data URL decodes to. */
function dataUrlBytes(dataUrl: string): number {
  const b64 = dataUrl.slice(dataUrl.indexOf(',') + 1);
  return Math.floor((b64.length * 3) / 4);
}

/** Decode a QR on the canvas — full size first, then a 50% copy (jsQR
 *  sometimes misses a big, noisy code that a smaller copy finds). */
export function decodeQr(canvas: HTMLCanvasElement): string | null {
  const tryOne = (c: HTMLCanvasElement) => {
    const ctx = c.getContext('2d');
    if (!ctx || !c.width || !c.height) return null;
    const { data, width, height } = ctx.getImageData(0, 0, c.width, c.height);
    const hit = jsQR(data, width, height, { inversionAttempts: 'attemptBoth' });
    const text = hit?.data?.trim();
    return text ? text : null;
  };
  const full = tryOne(canvas);
  if (full) return full;
  const half = draw(canvas, Math.max(1, Math.round(canvas.width / 2)), Math.max(1, Math.round(canvas.height / 2)));
  return tryOne(half);
}

/** A photo file → a downscaled JPEG data URL (< 3 MB) + the QR text. */
export async function prepareBadgePhoto(
  file: Blob,
): Promise<{ dataUrl: string; qr: string | null }> {
  const img = await loadImage(file);
  const w0 = img.naturalWidth || img.width;
  const h0 = img.naturalHeight || img.height;
  let scale = Math.min(1, MAX_SIDE / Math.max(w0, h0));
  let canvas = draw(img, Math.round(w0 * scale), Math.round(h0 * scale));

  let qr: string | null = null;
  try {
    qr = decodeQr(canvas);
  } catch {
    qr = null; // A QR we can't read is not fatal — the print still is.
  }

  let dataUrl = canvas.toDataURL('image/jpeg', 0.85);
  let quality = 0.85;
  while (dataUrlBytes(dataUrl) > MAX_BYTES && quality > 0.4) {
    quality -= 0.15;
    if (quality < 0.6) {
      scale *= 0.75;
      canvas = draw(img, Math.round(w0 * scale), Math.round(h0 * scale));
    }
    dataUrl = canvas.toDataURL('image/jpeg', quality);
  }
  return { dataUrl, qr };
}
